import { resolvePath } from "../layout/resolution.js";
import type {
  ComponentNode,
  CustomDisciplineInputComponentProps,
} from "../layout/types.js";
import { applyPatchToCustomDisciplineInputNode } from "./applyRuntimePatches.js";
import { collectBehaviorDependencies } from "./compileEvents.js";
import { compileBehavior } from "./compileBehavior.js";
import {
  dependencyFromDataBindingPath,
  evaluateWithRenderCache,
} from "./renderCache.js";
import {
  applyRuntimeDomTargetFlags,
  runCompiledNodeBehavior,
  withCurrentNodeGeometry,
} from "./applyNodeBehavior.js";
import type {
  CompiledCustomDisciplineInputNode,
  CompileNodeContext,
  RenderCustomDisciplineInputNode,
  RenderCustomDisciplineInputOption,
  RuntimeContext,
} from "./types.js";
import { compileNodeStyle, transitionsTouchTopLeft } from "./helpers.js";
import { uniqueDependencies } from "./method.js";

function resolveOptions(
  props: CustomDisciplineInputComponentProps,
  ctx: RuntimeContext,
): RenderCustomDisciplineInputOption[] {
  if (props.optionsMode !== "behavior" || !props.optionSource) {
    return props.staticOptions ?? [];
  }

  const source = (ctx as Record<string, unknown>)[props.optionSource];
  let rows = Array.isArray(source) ? source : [];
  const referenceFilter = ctx.__customDisciplineReferenceFilter;
  const fieldKey = getCustomDisciplineInputRuntimeKey(props);
  if (referenceFilter && fieldKey) {
    rows = rows.filter((row) => referenceFilter(fieldKey, row));
  }
  const valuePath = props.optionValuePath || "id";
  const labelPath = props.optionLabelPath || "name";

  return rows.map((row) => ({
    value: String(resolvePath(row, valuePath) ?? ""),
    label: String(resolvePath(row, labelPath) ?? ""),
  }));
}

function getCustomDisciplineInputRuntimeKey(
  props: CustomDisciplineInputComponentProps,
): string {
  return String(props.fieldKey ?? props.key ?? "").trim();
}

function resolveValue(
  props: CustomDisciplineInputComponentProps,
  ctx: RuntimeContext,
): string {
  const fieldKey = getCustomDisciplineInputRuntimeKey(props);
  const fromInputValues = fieldKey
    ? (ctx.inputValues as Record<string, unknown> | undefined)?.[fieldKey]
    : undefined;

  const resolved = fromInputValues ?? props.defaultValue;
  return resolved == null ? "" : String(resolved);
}

/**
 * Compiles a `customDisciplineInput` component node (custom discipline modal
 * layouts only). The node is a *controlled* input: its value is resolved from
 * `data.inputValues[fieldKey]` (falling back to `defaultValue`), and edits are
 * expected to flow back into `inputValues` via the host page (see
 * `CompiledLayoutRenderer`'s `onCustomDisciplineInputChange`).
 */
export function compileCustomDisciplineInputNode(
  node: ComponentNode,
  compileContext: CompileNodeContext,
): CompiledCustomDisciplineInputNode {
  if (node.kind !== "component" || node.component !== "customDisciplineInput") {
    throw new Error("Node is not a customDisciplineInput component");
  }

  const baseStyle = compileNodeStyle(node.style, node.transitions);
  const props = (node.props ?? {}) as CustomDisciplineInputComponentProps;
  const behavior = node.behavior
    ? compileBehavior(
        node.behavior,
        node.id,
        compileContext.catalogDeps,
        undefined,
        compileContext.layoutNodeById,
      )
    : undefined;

  const hasBehaviorOptions =
    props.optionsMode === "behavior" && Boolean(props.optionSource);
  const runtimeFieldKey = getCustomDisciplineInputRuntimeKey(props);

  const dependencies = uniqueDependencies([
    ...(behavior ? collectBehaviorDependencies(behavior, compileContext) : []),
    ...(runtimeFieldKey
      ? [dependencyFromDataBindingPath(`inputValues.${runtimeFieldKey}`)]
      : []),
    ...(hasBehaviorOptions
      ? [dependencyFromDataBindingPath(props.optionSource as string)]
      : []),
    // The reference-list filter (`referenceFilterBehavior`) is an opaque,
    // per-field graph the compiler cannot statically analyze here. Any of
    // these sources may gate an option, so re-run the filter whenever they
    // change rather than only on `optionSource` updates.
    ...(hasBehaviorOptions && props.type === "FOREIGN_KEY"
      ? [
          dependencyFromDataBindingPath("athlete"),
          dependencyFromDataBindingPath("inputValues"),
        ]
      : []),
  ]);

  return {
    id: node.id,
    dependencies,

    evaluate(ctx: RuntimeContext): RenderCustomDisciplineInputNode | null {
      return evaluateWithRenderCache(ctx, node.id, dependencies, () => {
        let renderNode: RenderCustomDisciplineInputNode = {
          id: node.id,
          key: ctx.__nodeInstanceKeyPrefix
            ? `${ctx.__nodeInstanceKeyPrefix}${node.id}`
            : node.id,
          x: node.x,
          y: node.y,
          width: node.width,
          height: node.height,
          zIndex: node.zIndex,
          rotation: node.rotation ?? 0,
          kind: "customDisciplineInput",
          props: {
            size: props.size,
            fieldKey: props.fieldKey,
            key: props.key,
            label: props.label,
            type: props.type,
            defaultValue: props.defaultValue,
            required: props.required,
            readOnly: props.readOnly,
            emptyOptionsText: props.emptyOptionsText,
            disableWhenEmpty: props.disableWhenEmpty,
            labelShrink: props.labelShrink,
            optionValuePath: props.optionValuePath,
            optionLabelPath: props.optionLabelPath,
          },
          value: resolveValue(props, ctx),
          options: resolveOptions(props, ctx),
          style: { ...baseStyle },
          hasTopLeftTransition: transitionsTouchTopLeft(node.transitions),
        };

        if (behavior) {
          const behaviorCtx = withCurrentNodeGeometry(ctx, {
            x: node.x,
            y: node.y,
            width: node.width,
            height: node.height,
            rotation: node.rotation ?? 0,
          });
          const { patches, effects, continuations, runtimeDomTarget } =
            runCompiledNodeBehavior(behavior, behaviorCtx, node.id);

          for (const patch of patches) {
            renderNode = applyPatchToCustomDisciplineInputNode(
              renderNode,
              patch,
            );
          }

          ctx.__runtimeEffects?.push(...effects);
          ctx.__runtimeContinuations?.push(...continuations);
          renderNode = applyRuntimeDomTargetFlags(
            renderNode,
            runtimeDomTarget,
            behavior,
          );
        }

        if (renderNode.style.visibility === false) {
          return null;
        }

        return renderNode;
      });
    },
  };
}
